import {
    CreateExtArgs,
    CreateExtensionArgument,
    DmApis,
    Extension,
    ExtensionAPI,
    DalValue,
    pointerUtils
} from '@wix/document-manager-core'
import {ReportableError} from '@wix/document-manager-utils'
import type {NamedRange, Pointer} from '@wix/document-services-types'
import * as constants from '../constants/constants'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import type {RelationshipsAPI} from './relationships'
import {stripHashIfExists} from '../utils/refArrayUtils'
import _ from 'lodash'

const {getPointer} = pointerUtils
const {DATA_TYPES, VIEW_MODES} = constants

export type PartialNamedRange = Omit<NamedRange, 'id' | 'type'>
export interface NamedRangesAPI {
    get(namedRangePointer: Pointer): NamedRange
    list(): Pointer[]
    getNewItemPointer(): Pointer
    add(newNamedRangePointer: Pointer, namedRange: PartialNamedRange): void
    update(namedRangePointer: Pointer, namedRange: PartialNamedRange): void
    remove(namedRangePointer: Pointer): void
}

export type NamedRangesExtensionAPI = ExtensionAPI & {
    namedRanges: NamedRangesAPI
}

const createExtensionAPI = ({dal, pointers, extensionAPI}: CreateExtArgs): NamedRangesExtensionAPI => {
    const dataModel = (extensionAPI as DataModelExtensionAPI).dataModel.withSuper
    const {relationships} = extensionAPI as RelationshipsAPI

    const MASTER_PAGE_ID = 'masterPage'
    const breakpointVariantsQuery = 'breakpointVariantsQuery'

    const getMasterPage = () => pointers.structure.getMasterPage(VIEW_MODES.DESKTOP)

    const getBreakpointsQueryPointer = (pageOrMasterpagePointer: Pointer): Pointer =>
        pointers.getInnerPointer(pageOrMasterpagePointer, breakpointVariantsQuery)
    const getBreakpointsDataPointer = (pageOrMasterpagePointer: Pointer): DalValue => {
        const breakpointsQueryPointer = getBreakpointsQueryPointer(pageOrMasterpagePointer)
        if (dal.has(breakpointsQueryPointer)) {
            const dataId = dal.get(breakpointsQueryPointer)
            return pointers.getPointer(dataId, DATA_TYPES.variants)
        }
    }
    const getOrCreateNamedRangesList = (pageOrMasterpagePointer: Pointer): Pointer => {
        const breakpointsDataPointer = getBreakpointsDataPointer(pageOrMasterpagePointer)

        if (breakpointsDataPointer) {
            return breakpointsDataPointer
        }

        const breakpointsData = {
            type: 'BreakpointsData',
            values: [],
            namedRanges: [],
            componentId: pageOrMasterpagePointer.id
        }
        const newItemPointer = dataModel.addItem(breakpointsData, DATA_TYPES.variants, pageOrMasterpagePointer.id)
        const breakpointsQueryPointer = getBreakpointsQueryPointer(pageOrMasterpagePointer)
        dal.set(breakpointsQueryPointer, newItemPointer.id)
        return newItemPointer
    }

    const list = (): Pointer[] => {
        const breakpointsDataPointer = getBreakpointsDataPointer(getMasterPage())
        if (!breakpointsDataPointer) {
            return []
        }

        const breakpointsData = dal.get(breakpointsDataPointer)

        if (!breakpointsData.namedRanges || breakpointsData.namedRanges.length === 0) {
            return []
        }

        return breakpointsData.namedRanges.map((ref: string) =>
            pointers.data.getVariantsDataItem(stripHashIfExists(ref))
        )
    }

    const get = (namedRangePointer: Pointer): NamedRange => {
        const namedRange = dal.get(namedRangePointer)

        if (!namedRange || namedRange.type !== 'NamedRange') {
            throw new ReportableError({
                message: `NamedRange ${namedRangePointer.id} does not exist`,
                errorType: 'nonExistentNamedRange'
            })
        }

        return namedRange as NamedRange
    }

    const validateNamedRange = (namedRange: Partial<NamedRange>) => {
        if (!namedRange.name) {
            throw new ReportableError({
                message: 'NamedRange name is required',
                errorType: 'invalidNamedRange'
            })
        }

        if (namedRange.name.length > 30) {
            throw new ReportableError({
                message: 'NamedRange name must be at most 30 characters',
                errorType: 'invalidNamedRange'
            })
        }
    }

    const validateNameUniqueness = (name: string, excludeId?: string) => {
        const existingPointers = list()
        const itemWithThisName = existingPointers.find(ptr => {
            return (dal.get(ptr) as NamedRange).name === name
        })

        if (itemWithThisName && !(excludeId && itemWithThisName.id === excludeId)) {
            throw new ReportableError({
                message: `NamedRange with name '${name}' already exists`,
                errorType: 'duplicateNamedRange'
            })
        }
    }

    const validateNamedRangeInScope = (namedRangePointer: Pointer, scope: Pointer) => {
        const breakpointsDataPointer = getBreakpointsDataPointer(scope)
        if (breakpointsDataPointer) {
            const breakpointsData = dal.get(breakpointsDataPointer)
            const namedRangeRef = `#${namedRangePointer.id}`
            if (breakpointsData.namedRanges.includes(namedRangeRef)) {
                return
            }
        }

        throw new ReportableError({
            message: `NamedRange ${namedRangePointer.id} does not exist for ${scope.id}`,
            errorType: 'namedRangeNotInScope'
        })
    }
    const getNewItemPointer = () => {
        const namedRangeId = dataModel.generateUniqueIdByType(DATA_TYPES.variants, MASTER_PAGE_ID, dal, pointers)
        return getPointer(namedRangeId, DATA_TYPES.variants)
    }

    const add = (newNamedRangePointer: Pointer, namedRange: PartialNamedRange): void => {
        validateNamedRange(namedRange)
        validateNameUniqueness(namedRange.name)

        const {id: namedRangeId} = newNamedRangePointer
        const fullNamedRange: NamedRange = {
            type: 'NamedRange',
            id: namedRangeId,
            ...namedRange
        }

        dataModel.addItem(fullNamedRange, DATA_TYPES.variants, MASTER_PAGE_ID, namedRangeId)

        const breakpointsDataPointer = getOrCreateNamedRangesList(getMasterPage())
        const breakpointsData = dal.get(breakpointsDataPointer) as any

        const updatedValues = [...(breakpointsData.namedRanges || []), `#${namedRangeId}`]
        dal.set(breakpointsDataPointer, {
            ...breakpointsData,
            namedRanges: updatedValues
        })
    }

    const update = (namedRangePointer: Pointer, namedRange: PartialNamedRange): void => {
        validateNamedRangeInScope(namedRangePointer, getMasterPage())
        validateNamedRange(namedRange)
        validateNameUniqueness(namedRange.name, namedRangePointer.id)

        const existingNamedRange = get(namedRangePointer)
        const updatedNamedRange: NamedRange = {
            ...existingNamedRange,
            ...namedRange,
            type: 'NamedRange',
            id: existingNamedRange.id
        }

        dal.set(namedRangePointer, updatedNamedRange)
    }

    const isNamedRangeUsed = (namedRangePointer: Pointer): boolean => {
        const references = relationships.getReferencesToPointer(namedRangePointer)
        const owningRefs = relationships.getOwningReferencesToPointer(namedRangePointer)

        return _.differenceBy(references, owningRefs, 'id').length > 0
    }

    const remove = (namedRangePointer: Pointer): void => {
        validateNamedRangeInScope(namedRangePointer, getMasterPage())
        const namedRange = get(namedRangePointer)

        if (isNamedRangeUsed(namedRangePointer)) {
            throw new ReportableError({
                message: `NamedRange ${namedRange.name} is currently in use and cannot be removed`,
                errorType: 'namedRangeInUse'
            })
        }

        const breakpointsDataPointer = getBreakpointsDataPointer(getMasterPage())
        const namedRangesList = dal.get(breakpointsDataPointer) as any

        if (namedRangesList) {
            const updatedValues = _.without(namedRangesList.namedRanges, `#${namedRangePointer.id}`)

            dal.set(breakpointsDataPointer, {
                ...namedRangesList,
                namedRanges: updatedValues
            })
        }

        dal.remove(namedRangePointer)
    }

    return {
        //if we add a page-level api for namedRanges, we will need to inject the masterPage pointer for the site level ones
        namedRanges: {
            get,
            list,
            getNewItemPointer,
            add,
            update,
            remove
        }
    }
}

const createExtension = ({}: CreateExtensionArgument): Extension => ({
    name: 'namedRanges',
    dependencies: new Set(['dataModel', 'relationships', 'dal']),
    createExtensionAPI,
    createPublicAPI: ({extensionAPI}: DmApis) => {
        const {namedRanges} = extensionAPI as NamedRangesExtensionAPI

        return {
            site: {
                namedRanges: {
                    get: namedRanges.get,
                    list: namedRanges.list,
                    add: namedRanges.add,
                    update: namedRanges.update,
                    remove: namedRanges.remove,
                    getNewItemPointer: namedRanges.getNewItemPointer
                }
            }
        }
    }
})

export {createExtension}
